import type { Asset, Draft, Surface } from '../shared/types';
import { fitDraft, placementCorners, type Point } from '../shared/paint';

/** Clockwise from top-left, in the artwork's unrotated coordinates. */
export type PlacementCorner = 0 | 1 | 2 | 3;

/** Project a mouse drag onto the rotated diagonal, preserving aspect ratio and
 * the opposite corner. Delta is in scene units, so grabbing anywhere in a
 * handle's hit target does not cause a jump. Shared limits still permit clipping. */
export function resizeDraftCorner(surface: Surface, asset: Asset, draft: Draft, corner: PlacementCorner, delta: Point): Draft {
  if (![delta.x, delta.y].every(Number.isFinite)) return draft;
  const corners = placementCorners(draft), opposite = (corner + 2) % 4;
  const anchor = corners[opposite], diagonal = { x: corners[corner].x - anchor.x, y: corners[corner].y - anchor.y };
  const scale = Math.max(0, 1 + (delta.x * diagonal.x + delta.y * diagonal.y) / (draft.width ** 2 + draft.height ** 2));
  const sized = fitDraft(surface, asset, 0, 0, Math.max(draft.width, draft.height) * scale, draft.rotation, draft.opacity);
  const offset = placementCorners({ ...sized, x: 0, y: 0 })[opposite];
  return fitDraft(surface, asset, anchor.x - offset.x, anchor.y - offset.y, Math.max(sized.width, sized.height), draft.rotation, draft.opacity);
}

/** Small sticky quarter-turn points keep exact rotations easy without restricting free rotation. */
export function snapPlacementRotation(value:number,tolerance=4,fine=false):number {
 if(fine||!Number.isFinite(value))return value;
 const nearest=Math.round(value/90)*90;
 return Math.abs(value-nearest)<=tolerance?(nearest===0?0:nearest):value;
}
export function rotateDraftHandle(draft:Draft,origin:Point,current:Point):Draft {
 const center={x:draft.x+draft.width/2,y:draft.y+draft.height/2};
 const angle=(point:Point)=>Math.atan2(point.y-center.y,point.x-center.x)*180/Math.PI;
 const rotation=snapPlacementRotation((draft.rotation??0)+angle(current)-angle(origin));
 return {...draft,rotation:((rotation+540)%360)-180};
}


function rotatedPoint(point:Point,degrees:number):Point {
 const angle=degrees*Math.PI/180;
 return {x:point.x*Math.cos(angle)-point.y*Math.sin(angle),y:point.x*Math.sin(angle)+point.y*Math.cos(angle)};
}
/** Placement hit testing uses the oriented source rectangle, including transparent pixels. */
export function containsDraftPoint(draft:Draft,point:Point):boolean {
 const local=rotatedPoint({x:point.x-draft.x-draft.width/2,y:point.y-draft.y-draft.height/2},-(draft.rotation??0));
 return Math.abs(local.x)<=draft.width/2&&Math.abs(local.y)<=draft.height/2;
}
/** A source drag/pinch keeps the grabbed source point under the moving midpoint. */
export function transformDraftGesture(surface:Surface,asset:Asset,draft:Draft,origin:Point,current:Point,scale=1,rotationDelta=0):Draft {
 if(![origin.x,origin.y,current.x,current.y,scale,rotationDelta].every(Number.isFinite))return draft;
 const edge=Math.max(draft.width,draft.height)*Math.max(0,scale),rotation=(draft.rotation??0)+rotationDelta;
 const sized=fitDraft(surface,asset,draft.x,draft.y,edge,rotation,draft.opacity);
 const anchor=rotatedPoint({x:origin.x-draft.x-draft.width/2,y:origin.y-draft.y-draft.height/2},-(draft.rotation??0));
 const offset=rotatedPoint({x:anchor.x/draft.width*sized.width,y:anchor.y/draft.height*sized.height},rotation);
 return {...draft,...fitDraft(surface,asset,current.x-sized.width/2-offset.x,current.y-sized.height/2-offset.y,edge,rotation,draft.opacity),paintOrder:draft.paintOrder,belowPlacementId:draft.belowPlacementId};
}
